import React from 'react';

import { MemberItem } from './MemberItem';
import type { MembersListProps } from './types';

import './MembersList.css';

export const MembersList = React.memo(function MembersList({
  members,
  currentUserTelegramId,
  canEdit,
  renderName,
  renderAvatar,
  renderRole,
  onConfirm,
  onLeave,
  onRemove,
  onRemoveReservation,
  capacity,
  canJoin,
  onJoin,
  canReserve,
  onReserve,
  headerLabel = 'Участники',
  maxVisibleCollapsed = 7,
  defaultExpanded = false,
  collapsible = false,
  defaultVisible,
}: MembersListProps) {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const [isListVisible, setIsListVisible] = React.useState(defaultVisible ?? !collapsible);

  // Sort members: 1) current user first, 2) confirmed, 3) pending
  const sortedMembers = React.useMemo(() => {
    return [...members].sort((a, b) => {
      // Current user always first
      if (currentUserTelegramId) {
        if (a.telegramId === currentUserTelegramId) return -1;
        if (b.telegramId === currentUserTelegramId) return 1;
      }
      // Then confirmed members before pending
      const aConfirmed = a.status !== 'pending';
      const bConfirmed = b.status !== 'pending';
      if (aConfirmed && !bConfirmed) return -1;
      if (!aConfirmed && bConfirmed) return 1;
      return 0;
    });
  }, [members, currentUserTelegramId]);

  // Compute isExtra flag for duplicate telegramIds
  const membersWithFlags = React.useMemo(() => {
    const seenIds = new Set<string>();
    return sortedMembers.map(member => {
      const isExtra = seenIds.has(member.telegramId);
      seenIds.add(member.telegramId);
      return { ...member, isExtra };
    });
  }, [sortedMembers]);

  // For collapsible list: show maxVisibleCollapsed by default, all when expanded
  const shouldTruncate = membersWithFlags.length > maxVisibleCollapsed;
  const visibleMembers = isExpanded || !shouldTruncate
    ? membersWithFlags
    : membersWithFlags.slice(0, maxVisibleCollapsed);

  // For capacity: calculate empty slots
  const emptySlots = capacity
    ? capacity.maxCapacity - members.length
    : 0;

  // Count confirmed and pending members
  const confirmedCount = members.filter(m => m.status !== 'pending').length;
  const pendingCount = members.length - confirmedCount;

  // Build header text (only confirmed participants in count)
  const headerText = capacity
    ? `${headerLabel} (${confirmedCount}/${capacity.maxCapacity})`
    : `${headerLabel}: ${confirmedCount}`;

  // Find current user's pending status
  const currentUserMember = members.find(m => m.telegramId === currentUserTelegramId);
  const isPendingCurrentUser = currentUserMember?.status === 'pending';

  return (
    <div className="members-section">
      <div
        className={`members-header ${collapsible ? 'members-header--collapsible' : ''}`}
        onClick={collapsible ? () => setIsListVisible(!isListVisible) : undefined}
        role={collapsible ? 'button' : undefined}
        aria-expanded={collapsible ? isListVisible : undefined}
      >
        <span className="members-icon">{'\uD83D\uDC65'}</span>
        <span>{headerText}</span>
        {pendingCount > 0 && (
          <span className="members-pending-badge">
            <span className="members-pending-icon">{'\u23F3'}</span>
            <span>{pendingCount}</span>
          </span>
        )}
        {collapsible && (
          <span className={`members-toggle-icon ${isListVisible ? 'members-toggle-icon--expanded' : ''}`}>
            ▼
          </span>
        )}
      </div>
      {isListVisible && <div className="members-list">
        {visibleMembers.map((member, index) => (
          <MemberItem
            key={`${member.telegramId}-${index}`}
            member={member}
            isCurrentUser={member.telegramId === currentUserTelegramId}
            isPending={isPendingCurrentUser}
            canEdit={canEdit}
            renderName={renderName}
            renderAvatar={renderAvatar}
            renderRole={renderRole}
            onConfirm={onConfirm}
            onLeave={onLeave}
            onRemove={onRemove}
            onRemoveReservation={onRemoveReservation}
            isExtra={member.isExtra}
          />
        ))}

        {/* Show all button (when collapsed) */}
        {shouldTruncate && !isExpanded && (
          <button
            type="button"
            className="members-toggle-btn"
            onClick={() => setIsExpanded(true)}
          >
            Показать всех ({membersWithFlags.length})
          </button>
        )}

        {/* Collapse button (when expanded) */}
        {shouldTruncate && isExpanded && (
          <button
            type="button"
            className="members-toggle-btn members-toggle-btn--collapse"
            onClick={() => setIsExpanded(false)}
          >
            Свернуть список
          </button>
        )}

        {/* Empty slots for capacity mode */}
        {capacity && Array.from({ length: emptySlots }).map((_, index) => (
          <div
            key={`empty-${index}`}
            className={`member-item member-item--empty ${canJoin ? 'member-item--joinable' : ''} ${canReserve ? 'member-item--reservable' : ''}`}
            onClick={canJoin ? onJoin : canReserve ? onReserve : undefined}
          >
            <span className="member-icon member-icon--empty">{'\u25EF'}</span>
            <span className="member-name">
              {capacity.emptySlotLabel || 'Свободно'}
            </span>
          </div>
        ))}
      </div>}
    </div>
  );
});
